package org.multiplatform.kotfolio.pages.compose

import androidx.compose.runtime.Composable
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.textAlign
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ul

@Page("/compose/common_wrapper")
@Composable
fun CommonWrapperPage() {

    BlogLayout(
        PageProperties(
            "Common Wrapper",
            "Common wrapper class is Kotlin class. It is used to clean code, wrapper the code easy to handle response success, failed and loading."
        ),
        BlogProperties("Jetpack Compose", "Common Wrapper")
    ) {
        Column(
            horizontalAlignment = Alignment.Start
        ) {
            SpanText(
                text = "PascalCase",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The idiomatic way of naming constants in Compose is using PascalCase. That seems obvious for object declarations, but we also use this convention for immutable top-level properties (including those with const modifier) and for enum values. That gives us consistency between all the different ways of representing constants.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .textAlign(TextAlign.Start)
                    )
                }

                KotlinCode(
                    code = """
                        const val DefaultKeyName = "_defaultKey"
                        
                        val StructurallyEqual: ComparisonPolicy = StructurallyEqualsImpl()
                        
                        object ReferenceEqual: ComparisonPolicy {...}
                        
                        sealed class LoadResult<T> {
                            data object Loading: LoadResult<Nothing>()
                            class Done<T>(val result: T) : LoadResult<T>()
                            class Error(val cause: Throwable) : LoadResult<Nothing>()
                        }
                        
                        //Use Pascalcase for naming constants in Compose
                        enum class Status {
                            Idle, Busy
                        }
                    """.trimIndent()
                )
            }

            KotlinCode(
                code = """
                    sealed class RequestState<out T> {
                        data object Idle : RequestState<Nothing>()
                        data object Loading : RequestState<Nothing>()
                        data class Success<out T>(val data: T) : RequestState<T>()
                        data class Error(val message: String) : RequestState<Nothing>()

                        fun isIdle(): Boolean = this is Idle
                        fun isLoading(): Boolean = this is Loading
                        fun isError(): Boolean = this is Error
                        fun isSuccess(): Boolean = this is Success

                        fun getSuccessData() = (this as Success).data
                        fun getSuccessDataOrNull() = if (this.isSuccess()) this.getSuccessData() else null
                        fun getErrorMessage(): String = (this as Error).message
                    }

                    @Composable
                    fun <T> RequestState<T>.DisplayResult(
                        modifier: Modifier = Modifier,
                        onIdle: (@Composable () -> Unit)? = null,
                        onLoading: (@Composable () -> Unit)? = null,
                        onError: (@Composable (String) -> Unit)? = null,
                        onSuccess: @Composable (T) -> Unit,
                        transitionSpec: ContentTransform? = scaleIn(tween(durationMillis = 400))
                                + fadeIn(tween(durationMillis = 800))
                                togetherWith scaleOut(tween(durationMillis = 400))
                                + fadeOut(
                            tween(durationMillis = 800)
                        ),
                        backgroundColor: Color? = null
                    ) {
                        AnimatedContent(
                            modifier = modifier
                                .background(color = backgroundColor ?: Color.Unspecified),
                            targetState = this,
                            transitionSpec = {
                                transitionSpec ?: (EnterTransition.None togetherWith ExitTransition.None)
                            },
                            label = "Content Animation"
                        ) { state ->
                            Row(
                                modifier = Modifier.fillMaxWidth(),
                                verticalAlignment = Alignment.CenterVertically,
                                horizontalArrangement = Arrangement.Center
                            ) {
                                when (state) {
                                    is RequestState.Idle -> {
                                        onIdle?.invoke()
                                    }

                                    is RequestState.Loading -> {
                                        onLoading?.invoke()
                                    }

                                    is RequestState.Error -> {
                                        onError?.invoke(state.getErrorMessage())
                                    }

                                    is RequestState.Success -> {
                                        onSuccess(state.getSuccessData())
                                    }
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    sealed class Resource<out T> {
                        data object Idle : Resource<Nothing>()
                        data object Loading : Resource<Nothing>()
                        data class Success<T>(val data: T) : Resource<T>()
                        data class Error(val message: String) : Resource<Nothing>()

                        @Composable
                        fun DisplayResult(
                            onIdle: (@Composable () -> Unit)? = null,
                            onLoading: @Composable () -> Unit,
                            onSuccess: @Composable (T) -> Unit,
                            onError: @Composable (String) -> Unit,
                        ) {
                            AnimatedContent(
                                targetState = this,
                                transitionSpec = {
                                    FadeIn togetherWith FadeOut
                                },
                                label = "Content Animation"
                            ) { state ->
                                when (state) {
                                    is Idle -> {
                                        onIdle?.invoke()
                                    }

                                    is Loading -> {
                                        onLoading()
                                    }

                                    is Success -> {
                                        onSuccess(state.data)
                                    }

                                    is Error -> {
                                        onError(state.message)
                                    }
                                }
                            }
                        }
                    }
                    
                    
                    val FadeIn = fadeIn(animationSpec = tween(220, delayMillis = 90)) +
                            scaleIn(
                                initialScale = 0.92f,
                                animationSpec = tween(220, delayMillis = 90)
                            )

                    val FadeOut = fadeOut(animationSpec = tween(90))
                    """.trimIndent()
            )

            //https://abifarhan.medium.com/the-20-jetpack-compose-interview-questions-that-separate-seniors-from-everyone-else-2026-edition-a53a3d82265b
            KotlinCode(
                code = """
                    // Complete error handling pattern
                    sealed class UiState<out T> {
                        object Loading : UiState<Nothing>()
                        data class Success<T>(val data: T) : UiState<T>()
                        data class Error(
                            val message: String,
                            val exception: Throwable? = null,
                            val canRetry: Boolean = true
                        ) : UiState<Nothing>()
                    }

                    @Composable
                    fun <T> UiStateContainer(
                        uiState: UiState<T>,
                        onRetry: () -> Unit,
                        content: @Composable (T) -> Unit
                    ) {
                        when (uiState) {
                            is UiState.Loading -> {
                                Box(
                                    modifier = Modifier.fillMaxSize(),
                                    contentAlignment = Alignment.Center
                                ) {
                                    CircularProgressIndicator()
                                }
                            }
                            is UiState.Success -> {
                                content(uiState.data)
                            }
                            is UiState.Error -> {
                                ErrorScreen(
                                    message = uiState.message,
                                    canRetry = uiState.canRetry,
                                    onRetry = onRetry
                                )
                            }
                        }
                    }

                    // ViewModel with proper error handling
                    @HiltViewModel
                    class UserViewModel @Inject constructor(
                        private val repository: UserRepository
                    ) : ViewModel() {
                        
                        private val _uiState = MutableStateFlow<UiState<User>>(UiState.Loading)
                        val uiState = _uiState.asStateFlow()
                        
                        init {
                            loadUser()
                        }
                        
                        fun loadUser() {
                            viewModelScope.launch {
                                _uiState.value = UiState.Loading
                                
                                repository.getUser()
                                    .onSuccess { user ->
                                        _uiState.value = UiState.Success(user)
                                    }
                                    .onFailure { exception ->
                                        _uiState.value = UiState.Error(
                                            message = when (exception) {
                                                is IOException -> "Network error. Check your connection."
                                                is HttpException -> "Server error. Try again later."
                                                else -> "Unknown error occurred."
                                            },
                                            exception = exception,
                                            canRetry = exception !is UnauthorizedException
                                        )
                                    }
                            }
                        }
                    }

                    // Error boundary pattern
                    @Composable
                    fun ErrorBoundary(
                        onError: (Throwable) -> Unit = {},
                        content: @Composable () -> Unit
                    ) {
                        var error by remember { mutableStateOf<Throwable?>(null) }
                        
                        error?.let {
                            ErrorScreen(
                                message = it.message ?: "Unknown error",
                                onRetry = { error = null }
                            )
                            onError(it)
                            return
                        }
                        
                        // Catch composition errors
                        try {
                            content()
                        } catch (e: Exception) {
                            error = e
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Flow Combine",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/patrykandpatrick/liftapp.git
            KotlinCode(
                code = """
                    //domain/model/Loadable.kt
                    sealed interface Loadable<out T : Any> {
                        data class Success<T : Any>(val data: T) : Loadable<T>

                        data class Error<T : Any>(val error: Throwable) : Loadable<T>

                        data object Loading : Loadable<Nothing>
                    }

                    fun <T : Any> T.toLoadable(): Loadable<T> = Loadable.Success(this)
                    
                    //core/model.Loadable.kt
                    @Composable
                    fun <T : Any> Loadable<T>.Unfold(
                        modifier: Modifier = Modifier,
                        onLoading: @Composable BoxScope.() -> Unit = {},
                        onError: @Composable (BoxScope.(Throwable) -> Unit)? = { Error(message = it.getUIMessage()) },
                        onSuccess: @Composable BoxScope.(T) -> Unit,
                    ) {
                        Box(modifier) {
                            when (this@Unfold) {
                                is Loadable.Loading -> onLoading()
                                is Loadable.Error -> onError?.invoke(this, error)
                                is Loadable.Success -> onSuccess(data)
                            }
                        }
                    }

                    fun <T : Any> Loadable<T>.valueOrNull(): T? =
                        when (this) {
                            is Loadable.Success -> data
                            else -> null
                        }

                    fun <T : Any> Flow<T>.toLoadableStateFlow(
                        scope: CoroutineScope,
                        started: SharingStarted = SharingStarted.WhileSubscribed(),
                    ): StateFlow<Loadable<T>> =
                        map<T, Loadable<T>> { Loadable.Success(it) }
                            .catch { emit(Loadable.Error(it)) }
                            .stateIn(scope, started, Loadable.Loading)

                    fun <T : Any> Flow<Loadable<T>>.stateIn(
                        scope: CoroutineScope,
                        started: SharingStarted = SharingStarted.WhileSubscribed(),
                    ): StateFlow<Loadable<T>> = stateIn(scope, started, Loadable.Loading)
                    
                    
                    @HiltViewModel
                    class PlanViewModel
                    @Inject
                    constructor(
                        getPlanStateUseCase: GetPlanStateUseCase,
                        scope: CoroutineScope,
                        private val navigationCommander: NavigationCommander,
                        @PreferenceQualifier.ActivePlan private val activePlan: Preference<ActivePlan?>,
                    ) : ViewModel(scope) {

                        //1
                        val state: StateFlow<Loadable<PlanState>> =
                            getPlanStateUseCase().toLoadableStateFlow(viewModelScope)
                            
                        //2
                         val state =
                             combine(selectedDate.flatMapLatest { date -> getPlanScheduleItemUseCase(date)) { planItem ->
               
                             }
                             .toLoadableStateFlow(viewModelScope)
                    """.trimIndent()
            )

            SpanText(
                text = "@UnsafeVariance",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            SpanText(
                text = "Result Handle kotlinx.serialization JsonElement",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/music-assistant/mobile-app.git
            KotlinCode(
                code = """
                    data class Answer(
                        val json: JsonObject
                    ) {
                        val messageId: String? = json["message_id"]?.jsonPrimitive?.content
                        val result: JsonElement? = json["result"]
                        
                        inline fun <reified T : Any> resultAs(): T? = result?.let { myJson.decodeFromJsonElement(it) }
                    }
                    
                    import kotlinx.serialization.json.Json
                    val myJson = Json {
                        prettyPrint = true
                        encodeDefaults = true
                        ignoreUnknownKeys = true
                    }
                    
                    inline fun <reified T : Any> Result<Answer>.resultAs(): T? = getOrNull()?.resultAs()
                    
                    @Serializable
                    data class ServerMediaItem()
                    
                    //API Get Result
                    result.getOrNull()?.resultAs<SearchResult>()?.toAppMediaItemList()
                    
                    """.trimIndent()
            )

            SpanText(
                text = "@UnsafeVariance",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                        import androidx.compose.animation.AnimatedContent
                        import androidx.compose.animation.core.tween
                        import androidx.compose.animation.fadeIn
                        import androidx.compose.animation.fadeOut
                        import androidx.compose.animation.togetherWith
                        import androidx.compose.runtime.Composable

                        sealed class RequestState<out T> {
                            data object Idle : RequestState<Nothing>()
                            data object Loading : RequestState<Nothing>()
                            data class Success<T>(val data: T) : RequestState<T>()
                            data class Error(val message: String) : RequestState<Nothing>()

                            fun isLoading() = this is Loading
                            fun isSuccess() = this is Success
                            fun isError() = this is Error

                            /**
                             * Returns data from a [Success].
                             * @throws ClassCastException If the current state is not [Success]
                             *  */
                            fun getSuccessData() = (this as Success).data
                            fun getSuccessDataOrNull(): T? {
                                return try {
                                    (this as Success).data
                                } catch (e: Exception) {
                                    null
                                }
                            }

                            /**
                             * Returns an error message from an [Error]
                             * @throws ClassCastException If the current state is not [Error]
                             *  */
                            fun getErrorMessage() = (this as Error).message
                            fun getErrorMessageOrNull(): String? {
                                return try {
                                    (this as Error).message
                                } catch (e: Exception) {
                                    null
                                }
                            }

                            @Composable
                            fun DisplayResult(
                                onIdle: (@Composable () -> Unit)? = null,
                                onLoading: @Composable () -> Unit,
                                onSuccess: @Composable () -> Unit,
                                onError: @Composable () -> Unit,
                            ) {
                                AnimatedContent(
                                    targetState = this,
                                    transitionSpec = {
                                        fadeIn(tween(durationMillis = 300)) togetherWith
                                                fadeOut(tween(durationMillis = 300))
                                    },
                                    label = "Content Animation"
                                ) { state ->
                                    when (state) {
                                        is Idle -> {
                                            onIdle?.invoke()
                                        }

                                        is Loading -> {
                                            onLoading()
                                        }

                                        is Success -> {
                                            onSuccess()
                                        }

                                        is Error -> {
                                            onError()
                                        }
                                    }
                                }
                            }
                        }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                        @HiltViewModel
                        class HomeViewModel : ViewModel() {
                            val data = fetchDataWithWrapper()

                            private var subjectList = listOf("Kotlin", "Compose", "Multiplatform")
    
                            private fun fetchDataWithWrapper(): Flow<RequestState<List<String>>> {
                                return flow {
                                    emit(RequestState.Loading)
                                    delay(2000)
                                    emit(RequestState.Success(data = subjectList))
                                }
                            }
                        }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                        val viewModel: HomeViewModel = hiltViewModel()
                        val data by viewModel.data.collectAsState(initial = RequestState.Idle)

                        data.DisplayResult(
                            onLoading = { CircularProgressIndicator() },
                            onSuccess = { Text(data.getSuccessData().joinToString()) },
                            onError =   { Text(data.getErrorMessage()) })
                    """.trimIndent()
            )

            SpanText(
                text = "KOTLIN DEFAULT RESULT CLASS",
                modifier = Modifier.fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                        fun main() {
                            fun fetchData(): Result<String> {
                                return try {
                                    // Data retrieval call
                                    val data = "success"
                                    Result.success(data)
                                } catch (e: Exception) {
                                    Result.failure(e)
                                }
                            }

                            val result = fetchData()
                            if(result.isSuccess) {
                                println("Data successfully fetched!")
                                val data = result.getOrNull()
                            } else {
                                println("Data fetch encountered an error")
                                val error = result.exceptionOrNull()
                            }
                        }
                    """.trimIndent()
            )

            SpanText(
                text = "Response wrapper",
                modifier = Modifier.fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    @Serializable
                    data class DataResponse<T>(
                        @SerialName(value = "results") val data: T?,
                        @SerialName(value = "message") val errorMessage: String?,
                        @SerialName(value = "success") val success: @Polymorphic Any?
                    )
                    """.trimIndent()
            )
        }
    }
}

//https://github.com/music-assistant/mobile-app.git (Result Handle kotlinx.serialization JsonElement)
//https://github.com/xephosbot/AnilibriaRefresh (KSerializer)
//https://github.com/Luwaphemi/DataTruth.git (KSerializer)
//https://github.com/mikaere66/DiscoverMultiplatform (KSerializer)
//https://github.com/patrykandpatrick/liftapp.git
//https://github.com/michaelbull/kotlin-result (Kotlin Result Good)